import React from 'react';
import { Icon, Touchable, useTheme } from '@draftbit/ui';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { DefaultTheme, NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { I18nManager, Platform, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { systemWeights } from 'react-native-typography';
import LinkingConfiguration from './LinkingConfiguration';
import * as AuthenticationApi from './apis/AuthenticationApi.js';
import * as GlobalVariables from './config/GlobalVariableContext';
import checkExpires from './global-functions/checkExpires';
import checkScreenMode from './global-functions/checkScreenMode';
import timestampToISO from './global-functions/timestampToISO';
import AccountScreen from './screens/AccountScreen';
import AccountVerificationScreen from './screens/AccountVerificationScreen';
import BestPracticesScreen from './screens/BestPracticesScreen';
import DashboardScreen from './screens/DashboardScreen';
import ExpensesFormScreen from './screens/ExpensesFormScreen';
import FailureScreen from './screens/FailureScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import GetStartedScreen from './screens/GetStartedScreen';
import IncomeFormScreen from './screens/IncomeFormScreen';
import LoginScreen from './screens/LoginScreen';
import OverviewScreen from './screens/OverviewScreen';
import PaymentPlanSelectionScreen from './screens/PaymentPlanSelectionScreen';
import SignupScreen from './screens/SignupScreen';
import SuccessScreen from './screens/SuccessScreen';
import WhatIfScreen from './screens/WhatIfScreen';
import palettes from './themes/palettes';
import Breakpoints from './utils/Breakpoints';
import useWindowDimensions from './utils/useWindowDimensions';

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();
const Drawer = createDrawerNavigator();

function DefaultAndroidBackIcon({ tintColor }) {
  return (
    <View style={[styles.headerContainer, styles.headerContainerLeft]}>
      <Icon
        name="AntDesign/arrowleft"
        size={24}
        color={tintColor}
        style={[styles.headerIcon, styles.headerIconLeft]}
      />
    </View>
  );
}

function DefaultDrawerIcon({ tintColor, navigation }) {
  return (
    <Touchable
      onPress={() => navigation.toggleDrawer()}
      style={[styles.headerContainer, styles.headerContainerLeft]}
    >
      <Icon
        name="EvilIcons/navicon"
        size={27}
        color={tintColor}
        style={[styles.headerIcon, styles.headerIconLeft]}
      />
    </Touchable>
  );
}

export default function RootAppNavigator() {
  const theme = useTheme();

  const Constants = GlobalVariables.useValues();

  const dimensions = useWindowDimensions();

  return (
    <NavigationContainer
      theme={{
        ...DefaultTheme,
        colors: {
          ...DefaultTheme.colors,
          background: '#FFFFFF',
        },
      }}
      linking={LinkingConfiguration}
    >
      <Stack.Navigator
        screenOptions={({ navigation }) => ({
          cardStyle: { flex: 1 },
          headerBackImage:
            Platform.OS === 'android' ? DefaultAndroidBackIcon : null,
          headerShown: false,
        })}
      >
        <Stack.Screen
          name="LoginScreen"
          component={LoginScreen}
          options={({ navigation }) => ({
            title: 'Login',
          })}
        />
        <Stack.Screen
          name="SignupScreen"
          component={SignupScreen}
          options={({ navigation }) => ({
            title: 'Signup ',
          })}
        />
        <Stack.Screen
          name="GetStartedScreen"
          component={GetStartedScreen}
          options={({ navigation }) => ({
            title: 'Get Started',
          })}
        />
        <Stack.Screen
          name="DashboardScreen"
          component={DashboardScreen}
          options={({ navigation }) => ({
            title: 'Dashboard',
          })}
        />
        <Stack.Screen
          name="IncomeFormScreen"
          component={IncomeFormScreen}
          options={({ navigation }) => ({
            title: 'Income Form',
          })}
        />
        <Stack.Screen
          name="ExpensesFormScreen"
          component={ExpensesFormScreen}
          options={({ navigation }) => ({
            title: 'Expenses Form',
          })}
        />
        <Stack.Screen
          name="OverviewScreen"
          component={OverviewScreen}
          options={({ navigation }) => ({
            title: 'Overview',
          })}
        />
        <Stack.Screen
          name="WhatIfScreen"
          component={WhatIfScreen}
          options={({ navigation }) => ({
            title: 'What If',
          })}
        />
        <Stack.Screen
          name="AccountScreen"
          component={AccountScreen}
          options={({ navigation }) => ({
            title: 'Account',
          })}
        />
        <Stack.Screen
          name="ForgotPasswordScreen"
          component={ForgotPasswordScreen}
          options={({ navigation }) => ({
            title: 'Forgot Password',
          })}
        />
        <Stack.Screen
          name="BestPracticesScreen"
          component={BestPracticesScreen}
          options={({ navigation }) => ({
            title: 'Best Practices',
          })}
        />
        <Stack.Screen
          name="PaymentPlanSelectionScreen"
          component={PaymentPlanSelectionScreen}
          options={({ navigation }) => ({
            title: 'Payment Plan Selection',
          })}
        />
        <Stack.Screen
          name="SuccessScreen"
          component={SuccessScreen}
          options={({ navigation }) => ({
            title: 'Success',
          })}
        />
        <Stack.Screen
          name="FailureScreen"
          component={FailureScreen}
          options={({ navigation }) => ({
            title: 'Failure',
          })}
        />
        <Stack.Screen
          name="AccountVerificationScreen"
          component={AccountVerificationScreen}
          options={({ navigation }) => ({
            title: 'Account Verification',
          })}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    ...Platform.select({
      ios: null,
      default: {
        marginVertical: 3,
        marginHorizontal: 11,
      },
    }),
  },
  headerContainerLeft: Platform.select({ ios: { marginLeft: 8 } }),
  headerIcon: Platform.select({
    ios: {
      marginVertical: 12,
      resizeMode: 'contain',
      transform: [{ scaleX: I18nManager.isRTL ? -1 : 1 }],
    },
    default: {
      margin: 3,
      resizeMode: 'contain',
      transform: [{ scaleX: I18nManager.isRTL ? -1 : 1 }],
    },
  }),
  headerIconLeft: Platform.select({ ios: { marginRight: 6 } }),
});
